<script setup lang="ts">
  import { ref, onMounted, onBeforeUnmount } from "vue";
  import { useI18n } from "vue-i18n";
  import { Spinner, useNotification, IconSuccess, Button } from "@ui-kit";
  import { useBarcode, usePowerRfid, useReadRfid, useWriteRfid, useSound, LOCK_MEM_TYPE, SOUNDS } from "@plugins";
  import {
    useLazyProductsLoad,
    useGetWTFIR,
    WTFIRInputType,
    useLazyFindPairs,
    ErrorCode,
    useCheckCis,
    WTFIRTypeData,
    useSetPairs,
    useLazyGetPairAlarm,
    setPairs as setPairsApi,
    ResponseCode,
  } from "@src/api";
  import { PairService } from "@src/services";
  import { useSpinner, useProfileOptions } from "@src/hooks";
  import {
    ScanScreen,
    ScanScreenSuccess,
    ScanScreenError,
    ScanScreenStart,
    Alarming,
    RequestScanWithProductShortCard,
    Quarantine,
  } from "@src/components";
  import { sleep } from "@src/utils";

  const emit = defineEmits<{ "close": [value: void] }>();

  const barcode = useBarcode();
  const powerRfid = usePowerRfid();
  const readRfid = useReadRfid();
  const writeRfid = useWriteRfid();
  const sound = useSound();
  const { execute: getProduct, data: product } = useLazyProductsLoad();
  const { execute: getFindPairs, data: pairs } = useLazyFindPairs();
  const { execute: getWTFIR, isLoading: isWTFIRLoading } = useGetWTFIR();
  const { mutate: checkCis } = useCheckCis();
  const { mutateAsync: setPairs } = useSetPairs();
  const { execute: getPairAlarm } = useLazyGetPairAlarm();
  const { t } = useI18n();
  const { hasPermission, profile } = useProfileOptions();
  const { visible: visibleSpinner } = useSpinner([isWTFIRLoading]);
  const { show, clear } = useNotification();

  const isNotMarked = ref(false);
  const isQuarantine = ref(false);
  const stageInfo = ref<{ stage: string; step: string }>({ stage: "scanCis", step: "start" });
  const gtin = ref<string | null>(null);
  const scanCis = ref<string | null>(null);
  const scanEpc = ref<string | null>(null);
  const isEqualGtin = ref(false);
  const isEqualEpc = ref(false);

  const handleAddBarcodeListener = () => {
    barcode.subscribe(handleScanBarcode);
  };

  const handleRemoveListener = () => {
    readRfid.unsubscribe();
    barcode.unsubscribe();
  };

  const goToBeginningFlow = () => {
    stageInfo.value = { stage: "scanCis", step: "start" };
    handleRemoveListener();

    handleAddBarcodeListener();
  };

  const handleGoToError = (message?: string) => {
    if (message && stageInfo.value.step === "error") {
      show({ title: message, type: "error" });
    }

    stageInfo.value.step = "error";

    sound.playSound(SOUNDS.AUDIO_ERROR_DEFAULT);
  };

  const handleScanEpc = async (data: string) => {
    if (!data) {
      show({ title: t("EPC метка не найдена"), type: "error" });
      return;
    }

    const { data: wtfirData, isError: isWTFIRError } = await getWTFIR({ data, input_type: WTFIRInputType.rfid });

    if (isWTFIRError.value) {
      show({ title: t("Ошибка, попробуйте еще раз"), type: "error" });
      return;
    }

    if (wtfirData.value.type_data !== WTFIRTypeData.epc) {
      show({ title: t("Некорректный EPC"), type: "error" });
      return;
    }

    if (wtfirData.value.items.length === 0) {
      show({ title: t("Товар с данной EPC не найден"), type: "error" });
      return;
    }

    isEqualGtin.value = gtin.value === wtfirData.value.gtin;
    isEqualEpc.value = data === pairs.value?.pairs.at(0)?.epc;
    scanEpc.value = data;

    if (!isEqualGtin.value || (!isNotMarked.value && !isEqualEpc.value)) {
      handleGoToError(t("EPC метка от другого товара"));

      return;
    }

    if (hasPermission("mobile.pair_verify") && !isNotMarked.value) {
      const epc = pairs?.value.pairs.at(0)?.epc || null;
      const cis = pairs?.value.pairs.at(0)?.cis || null;

      setPairs({ pairs: [{ epc, cis }] });
    }

    readRfid.unsubscribe();
    subscribeToAlarm();

    stageInfo.value.stage = "writeAlarm";
    stageInfo.value.step = "start";
  };

  const subscribeToAlarm = async () => {
    powerRfid.set(profile.value.constants.rfid.power_alarm_read);
    const resultPairAlarm = await getPairAlarm({ epc: scanEpc.value });

    readRfid.subscribe(async (rfidData) => {
      if (!rfidData) {
        show({ title: t("Аларм не найден"), type: "warning" });
        return;
      }

      if (resultPairAlarm.data.value.epc_alarm === rfidData.value) {
        stageInfo.value.step = "success";
        readRfid.unsubscribe();

        return;
      }

      const { data, isError } = await getWTFIR({ data: rfidData.value, input_type: WTFIRInputType.rfid });

      if (isError.value) {
        show({ title: t("Ошибка, попробуйте еще раз"), type: "error" });
        return;
      }

      if (![WTFIRTypeData.epcAlarm, WTFIRTypeData.epcAlarmClear].includes(data.value.type_data)) {
        show({ title: t("Некорректный Аларм"), type: "error" });
        return;
      }

      let password = data.value.type_data === WTFIRTypeData.epcAlarm ? resultPairAlarm.data.value.key : undefined;

      powerRfid.set(profile.value?.constants.rfid.power_once_read);
      const resultWriteRfidData = await writeRfid.writeData({
        epc: resultPairAlarm.data.value.epc_alarm,
        password,
        filterData: rfidData.value,
      });

      if (!resultWriteRfidData) {
        const resultWriteRfidDataWithEmptyPassword = await writeRfid.writeData({
          epc: resultPairAlarm.data.value.epc_alarm,
          filterData: rfidData.value,
        });

        if (resultWriteRfidDataWithEmptyPassword) {
          password = undefined;
        } else {
          show({ title: t("Ошибка записи аларма"), type: "error" });

          return;
        }
      }

      const resultWriteRfidPassword = await writeRfid.writePassword({
        password,
        filterData: resultPairAlarm.data.value.epc_alarm,
        newPassword: resultPairAlarm.data.value.key,
      });

      if (!resultWriteRfidPassword) {
        show({ title: t("Ошибка записи буфера"), type: "error" });
        return;
      }

      const resultLockMem = await writeRfid.lockMem({
        password: resultPairAlarm.data.value.key,
        filterData: resultPairAlarm.data.value.epc_alarm,
        type: LOCK_MEM_TYPE.ALL,
      });

      if (!resultLockMem) {
        show({ title: t("Ошибка блокировки памяти"), type: "error" });
        return;
      }

      if (resultWriteRfidData && resultWriteRfidPassword && resultLockMem) {
        clear();
        stageInfo.value.step = "success";
        powerRfid.set(powerRfid.prevPower.value);
        readRfid.unsubscribe();

        return;
      }

      handleGoToError();
    });
  };

  const handleScanBarcode = async (data: string) => {
    gtin.value = null;

    const resultWTFIR = await getWTFIR({ data, input_type: WTFIRInputType.barcode });
    const productId = resultWTFIR.data.value.items?.at(0) || null;
    const isBarcode = resultWTFIR.data.value.type_data === WTFIRTypeData.barcode;

    if (resultWTFIR?.isError.value) {
      return;
    }

    if (
      !PairService.scanTypeCorrect(WTFIRInputType.barcode, resultWTFIR.data.value.type_data, [WTFIRTypeData.barcode]) ||
      !productId
    ) {
      handleGoToError(t("Некорректный КИЗ"));

      return;
    }

    const resultProduct = await getProduct({ item_id: [productId] });

    if (resultProduct.isError.value) {
      handleGoToError(t("Ошибка, попробуйте еще раз"));

      return;
    }

    if (isBarcode && resultProduct.data.value.items.at(0)?.is_marked === false) {
      isNotMarked.value = true;
    } else {
      isNotMarked.value = false;
      scanCis.value = data;

      if (isBarcode && resultProduct.data.value.items.at(0)?.is_marked === true) {
        show({ title: t("Сканируйте КИЗ"), type: "error" });
        return;
      }

      const resultPairs = await getFindPairs({ cises: [data] });

      if (resultPairs.isError.value === ErrorCode.NOT_FOUND) {
        handleGoToQuarantine();

        return;
      }

      if (resultPairs.isError.value && resultPairs.isError.value !== ErrorCode.NOT_FOUND) {
        handleGoToError(t("Ошибка, попробуйте еще раз"));
        return;
      }

      checkCis({ cis: data });
    }

    barcode.unsubscribe();

    stageInfo.value.step = "success";
    gtin.value = resultWTFIR.data.value.gtin;
    await sleep(300);

    readRfid.subscribe((data) => {
      handleScanEpc(data.value);
    });
    stageInfo.value.stage = "requestScan";
    stageInfo.value.step = "start";
  };

  const handleSetPairs = async () => {
    try {
      if (scanEpc.value) {
        const result = await setPairsApi({
          pairs: [
            {
              epc: scanEpc.value,
              cis: pairs.value.pairs.at(0)?.cis,
            },
          ],
        });

        if (result.code === ResponseCode.OK) {
          show({ title: t("Пара успешно установлена"), type: "success" });
          readRfid.unsubscribe();
          subscribeToAlarm();

          stageInfo.value.stage = "writeAlarm";
          stageInfo.value.step = "start";
        }
      }
    } catch (error) {
      if (error.message) {
        show({ title: error.message, type: "error" });
      }

      if (error.code === 406) {
        handleGoToQuarantine();
      }
    }
  };

  const handleGoToQuarantine = () => {
    isQuarantine.value = true;

    handleRemoveListener();
  };

  const handleAgain = () => {
    isQuarantine.value = false;

    handleAddBarcodeListener();
  };

  onMounted(handleAddBarcodeListener);
  onBeforeUnmount(handleRemoveListener);
</script>

<template>
  <Quarantine v-if="isQuarantine" @click:again="handleAgain" @close="emit('close')" />
  <div v-else class="widget__alarming" data-testid="widget-alarming">
    <ScanScreen
      v-if="stageInfo.stage === 'scanCis'"
      :modal="{ subtitle: $t('КИЗ алармированного товара') }"
      @close="emit('close')"
    >
      <Spinner v-if="visibleSpinner" style="width: 44px; height: 44px" />
      <ScanScreenStart v-else-if="stageInfo.step === 'start'" />
      <ScanScreenError v-else-if="stageInfo.step === 'error'" />
      <ScanScreenSuccess v-else-if="stageInfo.step === 'success'" />
    </ScanScreen>
    <RequestScanWithProductShortCard
      v-if="stageInfo.stage === 'requestScan'"
      :productId="product?.items.at(0)?.item_id"
      :step="stageInfo.step"
      @click:back="goToBeginningFlow"
      @click:ok="handleGoToQuarantine"
    >
      <Button
        v-if="hasPermission('mobile.pair_set')"
        asSquare
        style="flex-shrink: 0"
        :iconBefore="IconSuccess"
        variant="secondary"
        @click="handleSetPairs"
        data-testid="set-pair-button"
        :disabled="isNotMarked || !isEqualGtin || isEqualEpc"
      />
    </RequestScanWithProductShortCard>
    <Alarming
      v-if="stageInfo.stage === 'writeAlarm'"
      :step="stageInfo.step"
      @click:more="goToBeginningFlow"
      @click:cancel="emit('close')"
    />
  </div>
</template>

<style scoped>
  .widget__alarming {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    z-index: 1;
  }
</style>
